import { Dispatch, FunctionComponent, SetStateAction, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { ListPerson } from "../../../List/schema/list";
import { InboxLeadCompact } from "../../components/LeadCardCompact/InboxLeadCompact";
import { MessagesLoadError } from "../../components/MessagesLoadError/MessagesLoadError";
import { InboxDateDivider } from "./InboxDateDivider";
import { InboxEmptyScope } from "./InboxEmptyScope";
import { useLeadsPagingSentinel } from "../../utils/useLeadsPagingSentinel";

import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface InboxLeadsColumnProps {
	showLeads: boolean;
	onToggleShowLeads: () => void;
	/** Current page of the paginated inbox query — drives the "more to load" spinner. */
	page: number;
	isFetching: boolean;
	onLoadMore: () => void;
	selected: number[];
	setSelected: (selected: number[]) => void;
	createBlacklistIsLoading: boolean;
	bulkChangeStatusIsLoading: boolean;
	setShowBlacklistModal: Dispatch<SetStateAction<boolean>>;
	setShowDoneModal: Dispatch<SetStateAction<boolean>>;
	onSetCurrentLead: (index: number) => void;
	/** The applied list filter — drives the "this list has no replies" empty state. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
}

/**
 * The Inbox's left column: collapsible lead-card list with date dividers,
 * infinite scroll, and auto-advance support (extracted from `Inbox.tsx`).
 * Reads `inbox` / `currentLeadIndex` / `leavingInboxLeadId` from the list
 * slice; when the selection moves (e.g. the undo-send commit advanced to the
 * next reply), the active card is scrolled into view, and a lead marked as
 * leaving fades out before its removal.
 */
export const InboxLeadsColumn: FunctionComponent<InboxLeadsColumnProps> = ({
	showLeads,
	onToggleShowLeads,
	page,
	isFetching,
	onLoadMore,
	selected,
	setSelected,
	createBlacklistIsLoading,
	bulkChangeStatusIsLoading,
	setShowBlacklistModal,
	setShowDoneModal,
	onSetCurrentLead,
	selectedLists,
}) => {
	const { t } = useTranslation();
	const { inbox, currentLeadIndex, meta, leavingInboxLeadId, inboxLoadFailed } =
		useAppSelector(listSelector);
	const leadsEndRef = useRef<HTMLDivElement>(null);

	// --- Infinite scroll: load the next page when the end sentinel becomes visible ---
	useLeadsPagingSentinel({ items: inbox, sentinelRef: leadsEndRef, onLoadMore });

	// --- Auto-advance: keep the active card visible when the selection moves ---
	// (e.g. after an undo-send commit removed the sent lead). `block: "nearest"`
	// makes this a no-op when the card is already in view, so manual clicks
	// don't cause scroll jumps.
	const activeLeadId = inbox[currentLeadIndex]?.id;

	useEffect(() => {
		if (activeLeadId === undefined) {
			return;
		}

		document
			.querySelector(`#leads-container [data-lead-id="${activeLeadId}"]`)
			?.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, [activeLeadId]);

	return (
		<div
			className={`bg-white w-[380px] min-w-[380px] transition-all z-15 duration-300 h-full border-r pl-0 border-border-light pb-0 relative ${
				showLeads ? "translate-x-0" : "translate-x-[0] !w-[35px] !min-w-[35px]"
			}`}
		>
			<CollapseGrabber
				ariaLabel={showLeads ? t("basics.collapse") : t("basics.expand")}
				collapsed={!showLeads}
				edge="right"
				onToggle={onToggleShowLeads}
			/>
			<div className={classNames("overflow-x-hidden h-full", "flex flex-col")}>
				<div
					className="flex flex-col max-h-full h-full overflow-y-auto border-t border-border-light scrollbar-hide w-full"
					id="leads-container"
				>
					{inbox.map((row: ListPerson, index: number) => (
						<div
							key={`${row.id}-${row.prospect?.name}`}
							className={classNames(
								"transition-opacity duration-200",
								leavingInboxLeadId === row.id ? "opacity-0" : ""
							)}
							data-lead-id={row.id}
						>
							<InboxDateDivider current={row} previous={inbox[index - 1]} />

							<InboxLeadCompact
								isCollapsed={!showLeads}
								createBlacklistIsLoading={createBlacklistIsLoading}
								setShowBlacklistModal={setShowBlacklistModal}
								bulkChangeStatusIsLoading={bulkChangeStatusIsLoading}
								setShowDoneModal={setShowDoneModal}
								handleSetCurrentLead={onSetCurrentLead}
								row={row}
								selected={selected}
								setSelected={setSelected}
							/>
						</div>
					))}

					{showLeads && !isFetching && !inboxLoadFailed && inbox.length === 0 && (
						<InboxEmptyScope selectedLists={selectedLists} />
					)}

					{showLeads && <MessagesLoadError />}

					{!isFetching && !inboxLoadFailed && meta && page < meta.pageCount ? (
						<div ref={leadsEndRef} className="flex items-center justify-center">
							<Spinner color="#4d66f5" />
						</div>
					) : isFetching ? (
						<div className="flex items-center justify-center">
							<Spinner color="#4d66f5" />
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
